import {Observable, Observer, ReplaySubject, share, Subject} from "rxjs";
import "reflect-metadata";
import {DatePipe} from "@angular/common";
import {Validation, ValidationService} from "../../validation/validation.service";
import {StringUtils} from "../../utils/string-utils";

const importantMetadataKey = Symbol("entityData:important");
const dataTypeMetadataKey = Symbol("entityData:dataType");
const arrayMetadataKey = Symbol("entityData:array");
const fieldMetadataKey = Symbol("entityData:field");
const transientMetaDataKey = Symbol("entityData:transient");
const identifierMetaDataKey = Symbol("entityData:identifier");
const propertiesMetaDataKey = Symbol("entityData:persistedProperties");
const persistedPropertiesMetaDataKey = Symbol("entityData:persistedProperties");
const dateFieldMetaDataKey = Symbol("entityData:dateField");
const timeFieldMetaDataKey = Symbol("entityData:timeField");
const localDateFieldMetaDataKey = Symbol("entityData:localDateField");
const localDateTimeFieldMetaDataKey = Symbol("entityData:localDateTimeField");
const unrestrictedMetadataKey = Symbol("entityData:unrestricted");
const reloadableMetadataKey = Symbol("entityData:reloadable");
const nullableMetadataKey = Symbol("entityData:nullable");
const localiserMetaDataKey = Symbol("entityData:localiser");
const pathKeyMetaDataKey = Symbol("entityData:pathKey");

/**
 * important property decorator
 * Mark a non-persisted property as important. Important properties are copied by assign().
 * @param target
 * @param key
 * @param descriptor
 */
export function important(target: any, key: string | symbol, descriptor?: PropertyDescriptor) {
    Reflect.defineMetadata(importantMetadataKey, true, target, key);
    if (descriptor && descriptor.get) {
        descriptor.enumerable = true;
    }
}

/**
 * nullable component property decorator
 * Define an EntityData component as not requiring to be instantiated when referenced.
 * @param target
 * @param key
 */
export function nullable(target: any, key: string) {
    Reflect.defineMetadata(nullableMetadataKey, true, target, key);
}

/**
 * reloadable component property decorator
 * Define an EntityData component as a candidate for lazy-reloading when its identifier value is modified.
 * @param target
 * @param key
 */
export function reloadable(target: any, key: string) {
    Reflect.defineMetadata(reloadableMetadataKey, true, target, key);
}

/**
 * localiser component property decorator
 * Define an EntityData component which localises its parent.
 * As it uses the Reflect.getMetadata to obtain the property type the class declaration needs to precede the main entity class.
 * @param target
 * @param key
 */
export function localiser(target: any, key: string) {
    component(target, key);
    Reflect.defineMetadata(localiserMetaDataKey, key, target);
}

/**
 * collection property decorator
 * Define an EntityData collection.
 * @param entityDataType when it is of type EntityData
 */
export function collection(entityDataType?: typeof EntityData) {
    return function (target: any, key: string) {
        if (entityDataType) {
            Reflect.defineMetadata(dataTypeMetadataKey, entityDataType, target, key);
        }
        Reflect.defineMetadata(arrayMetadataKey, true, target, key);
        Reflect.defineMetadata(propertiesMetaDataKey,
            Reflect.getMetadata(propertiesMetaDataKey, target) ? Reflect.getMetadata(propertiesMetaDataKey, target).concat(key) : [key], target);
        Reflect.defineMetadata(persistedPropertiesMetaDataKey,
            Reflect.getMetadata(persistedPropertiesMetaDataKey, target) ? Reflect.getMetadata(persistedPropertiesMetaDataKey, target).concat(key) : [key], target);
    }
}

/**
 * date collection property decorator
 * Define an EntityData collection.
 * @param entityDataType when it is of type EntityData
 */
export function dateCollection(target: any, key: string | symbol) {
    Reflect.defineMetadata(arrayMetadataKey, true, target, key);
    Reflect.defineMetadata(propertiesMetaDataKey,
        Reflect.getMetadata(propertiesMetaDataKey, target) ? Reflect.getMetadata(propertiesMetaDataKey, target).concat(key) : [key], target);
    Reflect.defineMetadata(persistedPropertiesMetaDataKey,
        Reflect.getMetadata(persistedPropertiesMetaDataKey, target) ? Reflect.getMetadata(persistedPropertiesMetaDataKey, target).concat(key) : [key], target);

    Reflect.defineMetadata(dateFieldMetaDataKey, true, target, key);
}

/**
 * component property decorator
 * Define an EntityData component.
 * As it uses the Reflect.getMetadata to obtain the property type the class declaration needs to precede the main entity class.
 * @param target
 * @param key
 */
export function component(target: any, key: string) {
    Reflect.defineMetadata(dataTypeMetadataKey, Reflect.getMetadata("design:type", target, key), target, key);
    Reflect.defineMetadata(arrayMetadataKey, false, target, key);
    Reflect.defineMetadata(propertiesMetaDataKey,
        Reflect.getMetadata(propertiesMetaDataKey, target) ? Reflect.getMetadata(propertiesMetaDataKey, target).concat(key) : [key], target);
    Reflect.defineMetadata(persistedPropertiesMetaDataKey,
        Reflect.getMetadata(persistedPropertiesMetaDataKey, target) ? Reflect.getMetadata(persistedPropertiesMetaDataKey, target).concat(key) : [key], target);
}

/**
 * field property decorator
 * Mark a property as a persisted field.
 * @param target
 * @param key
 * @param descriptor
 */
export function field(target: any, key: string | symbol, descriptor?: PropertyDescriptor) {
    Reflect.defineMetadata(fieldMetadataKey, true, target, key);
    Reflect.defineMetadata(propertiesMetaDataKey,
        Reflect.getMetadata(propertiesMetaDataKey, target) ? Reflect.getMetadata(propertiesMetaDataKey, target).concat(key) : [key], target);
    Reflect.defineMetadata(persistedPropertiesMetaDataKey,
        Reflect.getMetadata(persistedPropertiesMetaDataKey, target) ? Reflect.getMetadata(persistedPropertiesMetaDataKey, target).concat(key) : [key], target);
    if (descriptor && descriptor.get) {
        descriptor.enumerable = true;
    }
}

/**
 * transient collection property decorator
 * Define a non-persisted EntityData collection.
 * @param entityDataType when it is of type EntityData
 */
export function transientCollection(entityDataType?: typeof EntityData) {
    return function (target: any, key: string) {
        important(target, key);
        if (entityDataType) {
            Reflect.defineMetadata(dataTypeMetadataKey, entityDataType, target, key);
        }
        Reflect.defineMetadata(arrayMetadataKey, true, target, key);
        Reflect.defineMetadata(transientMetaDataKey, true, target, key);
        Reflect.defineMetadata(propertiesMetaDataKey,
            Reflect.getMetadata(propertiesMetaDataKey, target) ? Reflect.getMetadata(propertiesMetaDataKey, target).concat(key) : [key], target);
    }
}

/**
 * transient component property decorator
 * Define a non-persisted EntityData component.
 * As it uses the Reflect.getMetadata to obtain the property type the class declaration needs to precede the main entity class.
 * @param target
 * @param key
 */
export function transientComponent(target: any, key: string) {
    important(target, key);
    Reflect.defineMetadata(dataTypeMetadataKey, Reflect.getMetadata("design:type", target, key), target, key);
    Reflect.defineMetadata(arrayMetadataKey, false, target, key);
    Reflect.defineMetadata(transientMetaDataKey, true, target, key);
    Reflect.defineMetadata(propertiesMetaDataKey,
        Reflect.getMetadata(propertiesMetaDataKey, target) ? Reflect.getMetadata(propertiesMetaDataKey, target).concat(key) : [key], target);
}

/**
 * transient field property decorator
 * Mark a property as a non-persisted field.
 * @param target
 * @param key
 * @param descriptor
 */
export function transientField(target: any, key: string | symbol, descriptor?: PropertyDescriptor) {
    important(target, key);
    Reflect.defineMetadata(fieldMetadataKey, true, target, key);
    Reflect.defineMetadata(transientMetaDataKey, true, target, key);
    Reflect.defineMetadata(propertiesMetaDataKey,
        Reflect.getMetadata(propertiesMetaDataKey, target) ? Reflect.getMetadata(propertiesMetaDataKey, target).concat(key) : [key], target);
}

/**
 * dateField property decorator
 * Mark a property as a date field.
 * @param target
 * @param key
 * @param descriptor
 */
export function dateField(target: any, key: string | symbol, descriptor?: PropertyDescriptor) {
    field(target, key, descriptor);
    Reflect.defineMetadata(dateFieldMetaDataKey, true, target, key);
}

/**
 * timeField property decorator
 * Mark a property as a time field.
 * @param format
 */
export function timeField(format: string = "HH:mm") {
    return function (target: any, key: string | symbol, descriptor?: PropertyDescriptor) {
        field(target, key, descriptor);
        Reflect.defineMetadata(timeFieldMetaDataKey, format, target, key);
    }
}

/**
 * localDateField property decorator
 * Mark a property as a local date field.
 * @param target
 * @param key
 * @param descriptor
 */
export function localDateField(target: any, key: string | symbol, descriptor?: PropertyDescriptor) {
    field(target, key, descriptor);
    Reflect.defineMetadata(dateFieldMetaDataKey, true, target, key);
    Reflect.defineMetadata(localDateFieldMetaDataKey, true, target, key);
}

/**
 * localDateTimeField property decorator
 * Mark a property as a local date/time field.
 * @param target
 * @param key
 * @param descriptor
 */
export function localDateTimeField(target: any, key: string | symbol, descriptor?: PropertyDescriptor) {
    field(target, key, descriptor);
    Reflect.defineMetadata(dateFieldMetaDataKey, true, target, key);
    Reflect.defineMetadata(localDateTimeFieldMetaDataKey, true, target, key);
}

/**
 * unrestricted property decorator
 * Mark a property as available for modification on an otherwise restricted localisable entity
 * @param target
 * @param key
 */
export function unrestricted(target: any, key: string | symbol) {
    Reflect.defineMetadata(unrestrictedMetadataKey, true, target, key);
}

/**
 * identifier property decorator
 * Define a property as the identifier for an EntityData class.
 * @param target
 * @param key
 * @param descriptor
 */
export function identifier(target: any, key: string, descriptor?: PropertyDescriptor) {
    field(target, key, descriptor);
    Reflect.defineMetadata(identifierMetaDataKey, key, target);
}

/**
 * transient identifier property decorator
 * Define a transient (unpersisted) property as the identifier for an EntityData class.
 * @param target
 * @param key
 */
export function transientIdentifier(target: any, key: string) {
    Reflect.defineMetadata(identifierMetaDataKey, key, target);
}

/**
 * pathKey property decorator
 * Override the path key for a property.
 * @param pathKey
 */
export function pathKey(pathKey: string) {
    return function (target: any, key: string) {
        Reflect.defineMetadata(pathKeyMetaDataKey, pathKey, target, key);
    }
}

export enum EntityDataStatus {
    LOADING, LOADED, PROVISIONAL, ASSIGNED, RELOADING, RETAIN
}

/**
 * EntityDataNode is the common interface for EntityData and EntityDataArray.
 */
export interface EntityDataNode {

    /**
     * A reference to the proxy object wrapping this node
     */
    ednProxy: any;

    /**
     * The status of the EntityDataNode
     */
    ednStatus: EntityDataStatus;

    /**
     * The parent EntityDataNode
     */
    ednParent: EntityDataNode;

    /**
     * The name of this node within its parent
     */
    ednNodeName: string;

    /**
     * An internal UUID for every node
     */
    ednUuid: string;

    /**
     * If this EntityDataNode is PROVISIONAL, marks it and its PROVISIONAL ancestors as the specified status.
     * @param status
     */
    confirm(status: EntityDataStatus): void;

    /**
     * @returns The root EntityData of this EntityDataNode
     */
    getRoot(): EntityData<any>;

    /**
     * Converts the specified path to use path keys
     * @param path
     */
    convertToActualPath(path: string): string;

    /**
     * @returns The path to this EntityDataNode (slash separated)
     */
    getPath(allowAnonymousParent?: boolean, allowTransient?: boolean, getActualPath?: boolean): string;

    /**
     * @returns The path to this EntityDataNode relative to the root (period separated)
     */
    getSubPath(): string;

    /**
     * Notify this EntityDataNode that the root path has been resolved, which may require deferred collection resolution to be initiated
     */
    rootPathResolved(): void;

    /**
     * Loads the given data to the nested collection (EntityDataArray) specified by the path
     * @param path
     * @param json
     */
    loadCollection(path: string, json: Array<any>): void;

    /**
     * Loads the given data to the nested component (EntityData) specified by the path.
     * The path may specify a component of another EntityData, or an entry in an EntityDataArray.
     * @param path
     * @param json
     */
    loadComponent(path: string, json: Array<any>): void;

    /**
     * Removes any provisional EntityData nodes within this node
     */
    removeProvisional(): void;

    /**
     * Marl all EntityData nodes within this node as loaded (typically used to prevent reloading)
     */
    markAsLoaded(): void;

    /**
     * Notify subscribers that this EntityDataNode requires resolution.
     * The resolution request will always be for the highest ancestor which is not in LOADED status.
     * @return true if the component can be resolved, false otherwise
     */
    requestResolution(force?: boolean): boolean;
}

export class EntityDataResolutionRequest {
    constructor(public path: string, public actualPath: string) {
    }
}

/**
 * EntityData is the base class for entity data objects used in models.
 */
export class EntityData<T> implements EntityDataNode {
    private static escMap = {
        "\"": "\\\"",
        "\\": "\\\\",
        "\b": "\\b",
        "\f": "\\f",
        "\n": "\\n",
        "\r": "\\r",
        "\t": "\\t"
    };
    private static escRE = /[\\"\u0000-\u001F\u2028\u2029]/g;
    entityDataType: typeof EntityData;
    ednProxy: any;
    ednStatus: EntityDataStatus;
    ednParent: EntityDataNode;
    ednNodeName: string;
    ednUuid: string;
    ednCollectionResolutionsObserver: Observer<EntityDataResolutionRequest>;
    ednComponentResolutionsObserver: Observer<EntityDataResolutionRequest>;
    /**
     * Record version used for optimistic locking.
     */
    @field version: number;
    private resolutionRequested: boolean;
    private validationPromise: Promise<Validation>;
    private validation: Validation;

    private _collectionResolutions: Observable<EntityDataResolutionRequest>;

    /**
     * Returns an observable sequence of collection paths requiring resolution (lazy loading).
     * @returns {Observable}
     */
    get collectionResolutions(): Observable<EntityDataResolutionRequest> {
        return this._collectionResolutions;
    }

    private _componentResolutions: Observable<EntityDataResolutionRequest>;

    /**
     * Returns an observable sequence of component paths requiring resolution (lazy loading).
     * @returns {Observable}
     */
    get componentResolutions(): Observable<EntityDataResolutionRequest> {
        return this._componentResolutions;
    }

    /**
     * Returns an observable that resolves when the component is loaded. It will always return the last value for the component.
     * @returns {Observable<T>}
     */
    public get componentLoaded(): Observable<{
        path: string,
        component: EntityData<any>
    }> {
        return this.$componentLoaded.asObservable();
    }

    private _$componentLoaded: ReplaySubject<{
        path: string,
        component: EntityData<any>
    }>;

    public get $componentLoaded(): ReplaySubject<{
        path: string,
        component: EntityData<any>
    }> {
        if (!this._$componentLoaded) {
            this._$componentLoaded = new ReplaySubject(1);
        }
        return this._$componentLoaded;
    }

    /**
     * Returns an observable that resolves when the collection is assigned or loaded. It will always return the last value for the array.
     * @returns {Observable<T>}
     */
    public get collectionLoaded(): Observable<{
        path: string,
        collection: EntityData<any>[]
    }> {
        return this.$collectionLoaded.asObservable();
    }

    private _$collectionLoaded: ReplaySubject<{
        path: string,
        collection: EntityData<any>[]
    }>;

    public get $collectionLoaded(): ReplaySubject<{
        path: string,
        collection: EntityData<any>[]
    }> {
        if (!this._$collectionLoaded) {
            this._$collectionLoaded = new ReplaySubject(1);
        }
        return this._$collectionLoaded;
    }

    /**
     * Returns an observable that emits when the a collection changes including assigning a new value or adding and removing items from the collection.
     * @param {T} Array that should be an EntityDataArray
     * @returns {Observable<T[]>}
     */
    public get collectionChanged(): Observable<{
        path: string,
        collection: EntityData<any>[]
    }> {
        return this.$collectionChanged.asObservable();
    }

    private _$collectionChanged: Subject<{
        path: string,
        collection: EntityData<any>[]
    }>;

    public get $collectionChanged(): Subject<{
        path: string,
        collection: EntityData<any>[]
    }> {
        if (!this._$collectionChanged) {
            this._$collectionChanged = new Subject();
        }
        return this._$collectionChanged;
    }

    /**
     * Factory method for EntityData subclasses - returns a proxy wrapped instance.
     * NOTE: EntityData subclasses should ALWAYS be created with this method.
     * @param entityDataType
     * @param status
     * @returns {EntityData}
     */

    public static create<T extends EntityData<T>>(entityDataType: typeof EntityData, status: EntityDataStatus = EntityDataStatus.LOADED): T {
        return <T>EntityData.createAsNode(entityDataType, undefined, undefined, status);
    }

    /**
     * Factory method for EntityData subclasses - returns a proxy wrapped instance.
     * Note - if parent is undefined, the new EntityData is assumed to be a root node, and will be initialised with resolution observers.
     * If parent is null, the observers will not be created.
     *
     * @param entityDataType
     * @param parent
     * @param nodeKey
     * @param status
     * @param $loaded
     * @returns {EntityData}
     */
    static createAsNode<T extends EntityData<T>>(
        entityDataType: typeof EntityData,
        parent?: EntityDataNode,
        nodeKey?: string | symbol,
        status?: EntityDataStatus
    ): T {
        let entityData: EntityData<T> = new entityDataType().initNode(entityDataType, parent, nodeKey, status);

        // If the entity status is PROVISIONAL, but it has auto-initialised properties, the mark it as ASSIGNED instead
        if (status === EntityDataStatus.PROVISIONAL) {
            for (let key of EntityData.getAllKeys(entityData)) {
                if (entityData.isTransient && entityData.isTransient(key)) {
                    continue;
                }
                let propertyValue: any = entityData[key];
                if (propertyValue !== undefined) {
                    entityData.confirm(EntityDataStatus.ASSIGNED);
                    break;
                }
            }
        }

        let proxy: T = <T>new Proxy(entityData, {

            /**
             * Initialises nested EntityData components or collections on access to allow binding to uninitialised nested values.
             * EntityData and EntityDataArray instances are created in a provisional state,
             * which will exclude them from a JSON string generated for the target entity.
             *
             * Access to an uninitialised array will trigger a collection lazy load request.
             *
             * Access to uninitialised non-EntityData properties of an ASSIGNED (as opposed to LOADED) EntityData component will trigger a component lazy load request.
             *
             * Provides the "proxyTarget" property, which gives access to the un-proxied EntityData object.
             * @param target
             * @param key
             * @returns {any}
             */
            get: (target: T, key: string | symbol) => {
                if (key === "proxyTarget") {
                    return target;
                }
                let propertyValue: any = target[key];
                if ((propertyValue === null || propertyValue === undefined) && !target.isNullable(key)) {
                    if (target.isEntityDataArray(key)) {
                        let entityDataArray: EntityDataArray = EntityDataArray.createAsNode(undefined, target.ednProxy, key, EntityDataStatus.PROVISIONAL);
                        target[key] = entityDataArray;
                        entityDataArray.fireCollectionChanged();
                        entityDataArray.requestResolution();
                    } else {
                        let entityDataType: typeof EntityData = target.getEntityDataType(key);
                        if (entityDataType) {
                            target[key] = EntityData.createAsNode(entityDataType, target.ednProxy, key, EntityDataStatus.PROVISIONAL);
                        }

                        // Lazy load the entity data on an attempt to retrieve a persisted property which has not been set.
                        // We only lazy load if the status of the entity data is ASSIGNED - this prevents a lazy load from being triggered while an entity is being populated by a load (status = LOADING).
                        if (propertyValue === undefined && target.ednStatus === EntityDataStatus.ASSIGNED && target.isPersisted(key)) {
                            (<EntityData<any>>target).requestResolution();
                        }
                    }
                }
                return target[key];
            },

            /**
             * Any attempt to directly assign an untyped or incorrectly typed value to a property which has been decorated as a component,
             * will instead create the property as the appropriate EntityData class, and call Object.assign for the property.
             * Similarly, for arrays of entities, the array will be created as an new EntityDataArray, and each entry will be created (if not the correct type) and assigned.
             * Once data is assigned to a provisional EntityData object, the EntityData is confirmed (marked as no longer provisional).
             * ans similarly
             * @param target
             * @param key
             * @param value
             * @param receiver
             * @returns true
             */
            set: (target: T, key: string | symbol, value: any, receiver: any) => {
                let entityDataType: typeof EntityData = target.getEntityDataType(key);
                if (target.isEntityDataArray(key)) {
                    if (value === null || value === undefined) {
                        target[key] = value;
                        return true;
                    }

                    // if the array is the same doesn't do anything
                    if (value === target[key]) {
                        return true;
                    }
                    let currentArray: any = target[key] ? target[key]["proxyTarget"] : null;

                    // create new array to hold the new data
                    let entityDataArray: EntityDataArray = EntityDataArray.createAsNode(target[key], target.ednProxy, key);
                    if (value instanceof Array) {
                        for (let item of value) {
                            if (entityDataType) {
                                let entityData: EntityData<any>;
                                if (item instanceof entityDataType && (!item.ednParent || item.ednParent === currentArray || item.ednStatus === EntityDataStatus.LOADED)) {
                                    // Use a cooked value directly if it is the right type and:
                                    // - it has no parent; or
                                    // - its parent is already set to the current array which we are replacing; or
                                    // - its status is LOADED (we don't want to trigger re-lazy-loading).
                                    entityData = item;

                                    // clean up any provisional data so that lazy loading can be triggered via the new parent if required
                                    entityData.removeProvisional();
                                    entityData.initNode(entityDataType, entityDataArray, undefined, item.ednStatus);
                                } else {
                                    // Cook the raw entry
                                    // The status of the cooked object is LOADING if the target EntityData itself is LOADING, or else ASSIGNED,
                                    // unless the ednDeepStatus flag has been set on the object.
                                    entityData = EntityData.createAsNode(entityDataType, entityDataArray);
                                    const deepStatus: boolean = item instanceof entityDataType && EntityData.hasDeepStatus(value);
                                    const status: EntityDataStatus = deepStatus ? EntityData.getDeepStatus(value)
                                        : target.ednStatus === EntityDataStatus.LOADING ? EntityDataStatus.LOADING
                                            : EntityDataStatus.ASSIGNED;
                                    EntityData.removeDeepStatus(value);
                                    entityData.assign(item, status, deepStatus, EntityData.isNoReload(value));
                                }
                                entityDataArray.push(entityData);
                            } else if (target.isDateField(key) && item != undefined && item.split) {
                                let a = item.split(/[^0-9]/);
                                let date;
                                if (a.length == 3) {
                                    date = new Date(Date.UTC(a[0], a[1] - 1, a[2]));
                                } else {
                                    date = new Date(Date.UTC(a[0], a[1] - 1, a[2], a[3], a[4], a[5], a[6]));
                                }
                                entityDataArray.push(date);
                            } else {
                                entityDataArray.push(item);
                            }
                        }
                        EntityData.removeNoReload(value);
                        target[key] = entityDataArray;
                    } else {
                        // For convenience, allow a single object to be assigned to the array (as a single entry)
                        if (entityDataType) {
                            let entityData: EntityData<any>;
                            if (value instanceof entityDataType && !(<EntityData<any>>value).ednParent) {
                                // Use a cooked value directly (if it is the right type and has no parent)
                                entityData = value;

                                // clean up any provisional data so that lazy loading can be triggered via the new parent if required
                                entityData.removeProvisional();
                                entityData.initNode(entityDataType, target, key, value.ednStatus);
                            } else {
                                // Cook the raw object
                                // The status of the cooked object is LOADING if the target EntityData itself is LOADING, or else ASSIGNED,
                                // unless the ednDeepStatus flag has been set on the object.
                                entityData = EntityData.createAsNode(entityDataType, entityDataArray);
                                const deepStatus: boolean = value instanceof entityDataType && EntityData.hasDeepStatus(value);
                                const status: EntityDataStatus = deepStatus ? EntityData.getDeepStatus(value)
                                    : target.ednStatus === EntityDataStatus.LOADING ? EntityDataStatus.LOADING
                                        : EntityDataStatus.ASSIGNED;
                                EntityData.removeDeepStatus(value);
                                entityData.assign(value, status, deepStatus, EntityData.isNoReload(value));
                            }
                            entityDataArray.push(entityData);
                        } else {
                            entityDataArray.push(value);
                        }
                        target[key] = entityDataArray;
                    }
                    target[key].fireCollectionLoaded();
                    target[key].fireCollectionChanged();
                } else if (entityDataType) {
                    if (value === null || value === undefined) {
                        if (target[key] && target[key]._$loaded) {
                            target[key] = EntityData.createAsNode(entityDataType, target.ednProxy, key, EntityDataStatus.PROVISIONAL);
                        } else {
                            target[key] = value;
                        }
                        return true;
                    }

                    let entityData: EntityData<any>;
                    if (value instanceof entityDataType && !(<EntityData<any>>value).ednParent) {
                        // Use a cooked value directly (if it is the right type and has no parent)
                        entityData = value;

                        // clean up any provisional data so that lazy loading can be triggered via the new parent if required
                        entityData.removeProvisional();
                        entityData.initNode(entityDataType, target, key, entityData.ednStatus);
                        target[key] = entityData;
                    } else {
                        // Cook the raw object.
                        // The status of the cooked object is LOADING if the target EntityData itself is LOADING, or else ASSIGNED,
                        // unless the ednDeepStatus flag has been set on the object.
                        entityData = EntityData.createAsNode(entityDataType, target.ednProxy, key);
                        const deepStatus: boolean = value instanceof entityDataType && EntityData.hasDeepStatus(value);
                        const status: EntityDataStatus = deepStatus ? EntityData.getDeepStatus(value)
                            : target.ednStatus === EntityDataStatus.LOADING ? EntityDataStatus.LOADING
                                : EntityDataStatus.ASSIGNED;
                        EntityData.removeDeepStatus(value);
                        entityData.assign(value, status, deepStatus, EntityData.isNoReload(value));
                        target[key] = entityData;
                    }
                    target.confirm(entityData.ednStatus); // Sets status if currently PROVISIONAL
                } else if ((target.isDateField(key) || target.isTimeField(key) || Reflect.getMetadata("design:type", target, key) === Date) && value != undefined && value.split) {
                    if (value === null || value === undefined) {
                        target[key] = value;
                        return true;
                    }

                    let date: Date;
                    if (target.isTimeField(key)) {
                        date = EntityData.parseTime(value, target.getTimeFieldFormat(key));
                    } else if (target.isLocalDateField(key)) {
                        date = EntityData.parseLocalDate(value);
                    } else if (target.isLocalDateTimeField(key)) {
                        date = EntityData.parseLocalDateTime(value);
                    } else {
                        // To make date component compatible across ios and chrome and safari ...
                        let a = value.split(/[^0-9]/);
                        if (a.length === 3) {
                            date = new Date(Date.UTC(a[0], a[1] - 1, a[2]));
                        } else {
                            date = new Date(Date.UTC(a[0], a[1] - 1, a[2], a[3], a[4], a[5], a[6]));
                        }
                    }
                    target[key] = date;
                    target.confirm(EntityDataStatus.ASSIGNED); // Sets status if currently PROVISIONAL
                } else {
                    let currentValue: any = target[key];

                    // Modifying the identifier of a reloadable component triggers a reload
                    if (target.ednParent &&
                        !(target.ednParent instanceof Array) &&
                        (<EntityData<any>>target.ednParent).isReloadable(target.ednNodeName) &&
                        target.ednStatus !== EntityDataStatus.LOADING &&
                        target.getIdentifierKey() === key &&
                        value !== currentValue) {
                        target[key] = value;
                        if (value === null || value === undefined) {
                            target.removeFromParent();
                        } else {
                            target.ednStatus = EntityDataStatus.RELOADING;
                            (<EntityData<any>>target).requestResolution(true);
                        }
                    } else {
                        target[key] = value;
                        if (value != null && value != undefined) {
                            target.confirm(EntityDataStatus.ASSIGNED); // Sets status if currently PROVISIONAL
                        }
                    }
                }
                return true;
            }
        });
        entityData.ednProxy = proxy;
        proxy.onInit();
        return proxy;
    }

    public static retainStatus<T>(value: T): T {
        return EntityData.withDeepStatus(value, EntityDataStatus.RETAIN);
    }

    public static withDeepStatus<T>(value: T, status: EntityDataStatus): T {
        if (value && typeof value === "object") {
            value["ednDeepStatus"] = status;
        }
        return value;
    }

    public static hasDeepStatus<T>(value: T): boolean {
        return !!value["ednDeepStatus"];
    }

    public static getDeepStatus<T>(value: T): EntityDataStatus {
        return value["ednDeepStatus"];
    }

    public static removeDeepStatus<T>(value: T): void {
        if (value && typeof value === "object") {
            delete value["ednDeepStatus"];
        }
    }

    public static withNoReload<T>(value: T): T {
        if (value && typeof value === "object") {
            value["ednNoReload"] = true;
        }
        return value;
    }

    public static isNoReload<T>(value: T): boolean {
        return !!value["ednNoReload"];
    }

    public static removeNoReload<T>(value: T): void {
        if (value && typeof value === "object") {
            delete value["ednNoReload"];
        }
    }

    /**
     * Quick and dirty time parser with a limit number of formats - we should be using moment.js
     * @param {string} timeString
     * @return {Date}
     */
    public static parseTime(timeString: string, format: string = "HH:mm"): Date {
        if (timeString == "") {
            return null;
        }
        let time = timeString.match(/(\d+)(:(\d\d))?\s*(p?)/i);
        if (time == null) {
            return null;
        }

        let hours = parseInt(time[1], 10);
        if (!format.startsWith("HH")) {
            if (hours == 12 && !time[4]) {
                hours = 0;
            } else {
                hours += (hours < 12 && time[4]) ? 12 : 0;
            }
        }
        let d = new Date();
        d.setHours(hours);
        d.setMinutes(parseInt(time[3], 10) || 0);
        d.setSeconds(0, 0);
        return d;
    }

    /**
     * Quick and dirty time formatter with a limited number of formats - we should be using moment.js
     * @param {Date} time
     * @param {string} format
     * @return {string}
     */
    public static formatTime(time: Date, format: string): string {
        if (format === "hh:mm a") {
            return EntityData.zz(time.getHours() === 0 ? 12 : (time.getHours() % 12)) + ":" + EntityData.zz(time.getMinutes()) + (time.getHours() >= 12 ? " PM" : " AM");
        }
        if (format === "hh:mm:ss a") {
            return EntityData.zz(time.getHours() === 0 ? 12 : (time.getHours() % 12)) + ":" + EntityData.zz(time.getMinutes()) + ":" + EntityData.zz(time.getSeconds()) + (time.getHours() >= 12 ? " PM" : " AM");
        }
        if (format === "HH:mm") {
            return EntityData.zz(time.getHours()) + ":" + EntityData.zz(time.getMinutes());
        }
        return EntityData.zz(time.getHours()) + ":" + EntityData.zz(time.getMinutes()) + ":" + EntityData.zz(time.getSeconds());
    }

    public static formatLocalDate(date: Date): string {
        return date ? new DatePipe("en-GB").transform(date, "yyyy-MM-dd") : null;
    }

    public static formatLocalDateTime(date: Date): string {
        // We need to use en-GB for 24-hour times due to chrome's time formatting quirks.
        return date ? new DatePipe("en-GB").transform(date, "yyyy-MM-ddTHH:mm:ss") : null;
    }

    public static parseLocalDate(s: string): Date {
        const parts: number[] = s.split(/\D/).map((x) => parseInt(x));
        return new Date(parts[0], parts[1] - 1, parts[2]);
    }

    public static parseLocalDateTime(s: string): Date {
        const parts: number[] = s.split(/\D/).map((x) => parseInt(x));
        return new Date(parts[0], parts[1] - 1, parts[2], parts[3], parts[4], parts[5]);
    }

    /**
     * Converts a provided array of "raw" javascript objects to an array of "cooked" EntityData objects.
     * Note that the array itself is raw (it is not an EntityDataArray object).
     * @param data the raw data array (may also be a single object which will be placed into the returned array)
     * @param entityDataType
     * @returns the converted array
     */
    public static createArray<T extends EntityData<T>>(data: any, entityDataType: typeof EntityData): T[] {
        let array: EntityData<T>[] = [];
        if (data.constructor === Array) {
            for (let item of data) {
                let entityData: EntityData<T> = EntityData.createAsNode(entityDataType);
                entityData.assign(item);
                array.push(entityData);
            }
        } else {
            let entityData: EntityData<T> = EntityData.createAsNode(entityDataType);
            entityData.assign(data);
            array.push(entityData);
        }
        return <T[]>array;
    }

    public static matchesIdentifier(key: string, identifier: any): boolean {
        return identifier === undefined || identifier === null ? key === "undefined" : identifier.toString() === key;
    }

    public static getField(entity: any, fieldPath: string): any {
        return fieldPath ? fieldPath.split(".").reduce((component, fieldName) => component ? component[fieldName] : component, entity) : entity;
    }

    public static setField(entity: any, fieldPath: string, value: any): void {
        if (!fieldPath) {
            return;
        }
        let fieldList: string[] = fieldPath.split(".");
        let key: string = fieldList.pop();
        let target: any = fieldList.reduce((component, fieldName) => component ? component[fieldName] : component, entity);
        if (target) {
            target[key] = value;
        }
    }

    private static zz(n: number): string {
        if (n < 10) {
            return "0" + n;
        }
        return "" + n;
    }

    private static escJson(m) {
        return EntityData.escMap[m] || "\\u" + (m.charCodeAt(0) + 0x10000).toString(16).substring(1);
    };

    private static getAllKeys(obj: object | null): string[] {
        return (!obj || obj === Object.prototype) ? [] : [...new Set([
            ...this.getAllKeys(Object.getPrototypeOf(obj)),
            ...Object.getOwnPropertyNames(obj)
                .map(key => [key, Object.getOwnPropertyDescriptor(obj, key)] as const)
                .filter(([_, desc]) => (desc?.value !== undefined && typeof desc.value !== "function") || typeof desc?.get === "function")
                .map(([key, _]) => key)
        ])];
    }

    private static stringify(value: any, spaces: string = "  ", indent: string = "", timeFieldFormat?: string, isLocalDateField: boolean = false, isLocalDateTimeField: boolean = false): string {
        if (value == null) {
            return "null";
        }
        if (typeof value === "number") {
            return isFinite(value) ? value.toString() : "null";
        }
        if (typeof value === "boolean") {
            return value.toString();
        }
        if (value instanceof Date) {
            if (timeFieldFormat) {
                return "\"" + EntityData.formatTime(value, timeFieldFormat) + "\"";
            } else if (isLocalDateField) {
                return "\"" + EntityData.formatLocalDate(value) + "\"";
            } else if (isLocalDateTimeField) {
                return "\"" + EntityData.formatLocalDateTime(value) + "\""
            }
            return "\"" + value.toISOString() + "\"";
        }
        if (typeof value === "object") {
            if (value.ednStatus === EntityDataStatus.PROVISIONAL) {
                return null;
            }
            if (typeof value.toJSON === "function") {
                return EntityData.stringify(value.toJSON(), spaces, indent);
            }
            if (Array.isArray(value)) {
                let target: any = value["proxyTarget"] ?? value;
                let buffer: string[] = [];
                for (let i = 0; i < target.length; i++) {
                    let element: any = target[i];
                    if (element !== undefined) {
                        buffer.push(EntityData.stringify(element, spaces, indent));
                    }
                }
                if (buffer.length == 0) {
                    return "[]";
                }
                return "[ " + buffer.join(", ") + " ]";
            }
            if (toString.call(value) === "[object Object]") {
                let target: any = value["proxyTarget"] ?? value;
                let buffer: string[] = [];
                for (let key of EntityData.getAllKeys(value)) {
                    if (value.isTransient && value.isTransient(key)) {
                        continue;
                    }
                    let propertyValue: any = target[key];
                    if (propertyValue !== undefined) {
                        let timeFieldFormat: string = (value.isTimeField && value.isTimeField(key)) ? value.getTimeFieldFormat(key) : undefined;
                        let isLocalDate: boolean = value.isLocalDateField && value.isLocalDateField(key);
                        let isLocalDateTime: boolean = value.isLocalDateTimeField && value.isLocalDateTimeField(key);
                        let json: string = EntityData.stringify(propertyValue, spaces, indent + spaces, timeFieldFormat, isLocalDate, isLocalDateTime);
                        if (json !== undefined) {
                            buffer.push(EntityData.stringify(key) + ": " + json);
                        }
                    }
                }
                if (buffer.length == 0) {
                    return "{}";
                }
                return "{\n" + indent + spaces + buffer.join(",\n" + indent + spaces) + "\n" + indent + "}";
            }
        }
        return "\"" + value.toString().replace(EntityData.escRE, EntityData.escJson) + "\"";
    }

    /**
     * Initialises a node for inclusion in this entity data tree.
     *
     * Note - if parent is undefined, the new EntityData is assumed to be a root node, and will be initialised with resolution observers.
     * If parent is null, the observers will not be created.
     */
    initNode(
        entityDataType: typeof EntityData,
        parent?: EntityDataNode,
        nodeKey?: string | symbol,
        status?: EntityDataStatus
    ): EntityData<T> {
        this.entityDataType = entityDataType;
        this.ednParent = parent;
        this.ednNodeName = typeof nodeKey === "string" ? nodeKey : undefined;
        this.ednUuid = "edn-" + StringUtils.uuid();
        this.ednStatus = status ? status : parent ? parent.ednStatus : EntityDataStatus.LOADED;
        this.resolutionRequested = false;

        // The root EntityData creates observables of collection and component resolutions for lazy loading
        if (parent === undefined) {
            this._collectionResolutions = new Observable<EntityDataResolutionRequest>(observer => {
                this.ednCollectionResolutionsObserver = observer
            }).pipe(share());
            this._componentResolutions = new Observable<EntityDataResolutionRequest>(observer => {
                this.ednComponentResolutionsObserver = observer
            }).pipe(share());
        } else {
            this._collectionResolutions = undefined;
            this._componentResolutions = undefined;
        }

        return this;
    }

    /**
     * Checks for existence of the specified metadata key on a field within this entity data or one of its sub-components.
     *
     * @param metadataKey
     * @param {string} path Period-separated path of the field
     * @return {any}
     */
    public hasMetadata(metadataKey: any, path: string | symbol): boolean {
        if (typeof path === "string") {
            let i: number = path.indexOf(".");
            if (i >= 0) {
                let key: string = path.substring(0, i);
                let entityDataType: typeof EntityData = this.getEntityDataType(key);
                if (!entityDataType) {
                    return undefined;
                }
                return new entityDataType().hasMetadata(metadataKey, path.substring(i + 1));
            }
        }
        return Reflect.hasMetadata(metadataKey, this, path);
    }

    /**
     * Returns the value of the specified metadata key on a field within this entity data or one of its sub-components.
     *
     * @param metadataKey
     * @param {string} path Period-separated path of the field
     * @return {any}
     */
    public getMetadata(metadataKey: any, path: string | symbol): any {
        if (typeof path === "string") {
            let i: number = path.indexOf(".");
            if (i >= 0) {
                let entityDataType: typeof EntityData = this.getEntityDataType(path.substring(0, i));
                if (!entityDataType) {
                    return undefined;
                }

                // We do not want to access the actual value of the component as this may trigger lazy loading. Since we only need metadata, we can simply create a new instance.
                return new entityDataType().getMetadata(metadataKey, path.substring(i + 1));
            }
        }
        return Reflect.getMetadata(metadataKey, this, path);
    }

    public isImportant(path: string | symbol): boolean {
        return this.hasMetadata(importantMetadataKey, path) || (typeof path === "string" && path.startsWith("FLAG_"));
    }

    public getEntityDataType(path: string | symbol): any /* typeof EntityData */ {
        return this.getMetadata(dataTypeMetadataKey, path);
    }

    public isEntityDataArray(path: string | symbol): boolean {
        return this.getMetadata(arrayMetadataKey, path);
    }

    public isPersisted(path: string | symbol): boolean {
        return (this.hasMetadata(dataTypeMetadataKey, path) || this.hasMetadata(fieldMetadataKey, path) || this.hasMetadata(arrayMetadataKey, path)) && !this.hasMetadata(transientMetaDataKey, path);
    }

    public isTransient(key: string | symbol): boolean {
        return !this.isPersisted(key);
    }

    public isDateField(path: string | symbol): boolean {
        return this.hasMetadata(dateFieldMetaDataKey, path);
    }

    public isTimeField(path: string | symbol): boolean {
        return this.hasMetadata(timeFieldMetaDataKey, path);
    }

    public isLocalDateField(path: string | symbol): boolean {
        return this.hasMetadata(localDateFieldMetaDataKey, path);
    }

    public isLocalDateTimeField(path: string | symbol): boolean {
        return this.hasMetadata(localDateTimeFieldMetaDataKey, path);
    }

    public getTimeFieldFormat(path: string | symbol): string {
        return this.getMetadata(timeFieldMetaDataKey, path);
    }

    public getPathKey(path: string | symbol): string {
        return this.getMetadata(pathKeyMetaDataKey, path);
    }

    public isUnrestricted(path: string | symbol): boolean {
        return this.hasMetadata(unrestrictedMetadataKey, path);
    }

    public getIdentifierKey(): string {
        return Reflect.getMetadata(identifierMetaDataKey, this);
    }

    public getIdentifier(useUuid: boolean = false): any {
        let key: string = Reflect.getMetadata(identifierMetaDataKey, this);
        let identifier: any = key ? this[key] : undefined;
        return (useUuid && (identifier === undefined || identifier === null)) ? this.ednUuid : identifier;
    }

    public isReloadable(path: string | symbol): boolean {
        return this.hasMetadata(reloadableMetadataKey, path) && !EntityData.isNoReload(this);
    }

    public isNullable(path: string | symbol): boolean {
        return this.hasMetadata(nullableMetadataKey, path);
    }

    public getLocaliserKey(): string {
        return Reflect.getMetadata(localiserMetaDataKey, this);
    }

    public getLocaliser(): EntityData<any> {
        let key: string = Reflect.getMetadata(localiserMetaDataKey, this);
        return key ? this[key] : undefined;
    }

    public getFields(): string[] {
        return Reflect.getMetadata(propertiesMetaDataKey, this);
    }

    public getPersistedFields(): string[] {
        return Reflect.getMetadata(persistedPropertiesMetaDataKey, this);
    }

    confirm(status: EntityDataStatus): void {
        if (this.ednStatus === EntityDataStatus.PROVISIONAL) {
            this.ednStatus = status;
            if (this.ednParent) {
                this.ednParent.confirm(status);
            }
        }
    }

    getRoot(): EntityData<any> {
        return this.ednParent ? this.ednParent.getRoot() : this;
    }

    convertToActualPath(path: string): string {
        const i: number = path.indexOf(".");
        if (i >= 0) {
            const key = path.substring(0, i);
            return (this.getPathKey(key) || key) + "." + this[key].convertToActualPath(path.substring(i + 1));
        } else {
            return (this.getPathKey(path) || path);
        }
    }

    getPath(allowAnonymousParent: boolean = false, allowTransient: boolean = true, getActualPath: boolean = false): string {
        let identifier: any = this.getIdentifier(allowAnonymousParent);
        let nodeName: string = this.ednNodeName || identifier;
        if (this.ednParent) {
            if (!allowTransient && this.ednParent instanceof EntityData && this.ednNodeName && (<EntityData<any>>this.ednParent).isTransient(this.ednNodeName)) {
                const pathKey: string = (<EntityData<any>>this.ednParent).getPathKey(this.ednNodeName)
                if (!pathKey) {
                    return "@transient";
                }
                if (getActualPath) {
                    nodeName = pathKey;

                    // If the actual path is an array, add the identifier to the path of the transient
                    if ((<EntityData<any>>this.ednParent).isEntityDataArray(nodeName)) {
                        if (identifier == null) {
                            return "@transient";
                        }
                        nodeName = nodeName + "/" + identifier;
                    }
                }
            }
            let parentPath: string = this.ednParent.getPath(allowAnonymousParent, allowTransient, getActualPath);
            if (parentPath === "@transient") {
                return parentPath;
            }
            return ((parentPath || allowAnonymousParent) && nodeName) ? parentPath + "/" + nodeName : undefined;
        } else {
            return nodeName;
        }
    }

    getSubPath(): string {
        if (this.ednParent) {
            let parentSubPath: string = this.ednParent.getSubPath();
            return this.ednNodeName ? (parentSubPath ? parentSubPath + "." + this.ednNodeName : this.ednNodeName) : parentSubPath;
        } else {
            return undefined;
        }
    }

    /**
     * When the root path is resolved, notify all nested EntityDataNodes
     */
    rootPathResolved(): void {
        if (this.resolutionRequested) {
            this.requestResolution(true);
        } else if (this.ednStatus != EntityDataStatus.PROVISIONAL) {
            this.forEachComponentOrCollectionProperty((property: any) => {
                if (property.rootPathResolved) {
                    (<EntityData<any>>property).rootPathResolved();
                }
            });
        }
    }

    loadCollection(path: string, json: Array<any>): void {
        let parts: string[] = path.split("/");
        let key: string = parts.shift();
        if (!key) {
            return;
        }

        // For the root EntityData, the path will start with the identifier - verify and discard.
        if (!this.ednParent) {
            if (parts.length === 0 || key != this.getIdentifier(true)) {
                return;
            }
            key = parts.shift();
        }

        if (parts.length === 0) {
            // The final path element must reference an EntityDataArray - assign the json array to it.
            if (this.isEntityDataArray(key)) {
                this.ednStatus = EntityDataStatus.LOADING;
                this[key] = json;
                this[key].ednStatus = EntityDataStatus.LOADED;
                this.ednStatus = EntityDataStatus.LOADED;
            }
        } else {
            let property: any = this[key];
            property.loadCollection && property.loadCollection(parts.join("/"), json);
        }
    }

    loadComponent(path: string, json: any): void {
        let parts: string[] = path.split("/");
        let key: string = parts.shift();
        if (!key) {
            return;
        }

        // For the root EntityData, the path will start with the identifier - verify and discard.
        if (!this.ednParent) {
            if (parts.length === 0 || !EntityData.matchesIdentifier(key, this.getIdentifier(true))) {
                return;
            }
            key = parts.shift();
        }

        // If this is the final path element, then it must reference an EntityData - replace it with a new EntityData instance, and assign the json object to it.
        if (parts.length === 0) {

            // Check if the final path element specifies an identifier (key@identifier)
            let i: number = key.indexOf("@");
            let identifier: string;
            if (i >= 0) {
                identifier = key.substring(i + 1);
                key = key.substring(0, i);
            }

            let entityDataType: typeof EntityData = this.getEntityDataType(key);
            if (entityDataType && !this.isEntityDataArray(key)) {

                // If the final path element specifies an identifier, then it must match the existing identifier (if any) unless it is being reloaded
                let entityData: EntityData<any> = this[key];
                if (entityData) {
                    let currentIdentifier: any = entityData.getIdentifier();
                    if (identifier && currentIdentifier && !EntityData.matchesIdentifier(identifier, currentIdentifier)) {
                        return;
                    }
                }

                // We need to assign the json with a status of LOADING. As such, we cannot simply assign the json and allow the proxy setter to perform the assignment.
                // We create EntityData with a null parent, otherwise the proxy setter would still create and assign a new EntityData.
                // The parent will be properly set by the proxy setter.
                // Note - alternatively we could have bypassed the proxy by assigning to this["proxyTarget"][key]
                entityData = this[key] = EntityData.createAsNode(entityDataType, null, key);
                entityData.assign(json, EntityDataStatus.LOADING);
                entityData.fireComponentLoaded();
            }
        } else {
            let property: any = this[key];
            property.loadComponent && property.loadComponent(parts.join("/"), json);
        }
    }

    /**
     * Calls this.assign(data), but sets the status of the data to LOADING during the assignment, and then to LOADED upon completion.
     * The status of LOADING means that any created descendant will also be marked as LOADING.
     * Once the data is LOADED, any direct assignment to a component or array entry is marked as ASSIGNED by default.
     * In other words, the LOADING status indicates that a load is occurring and that all assigned data is "real" and does not require lazy resolution.
     * @param data
     */
    public load(data: any): EntityData<T> {
        this.assign(data, EntityDataStatus.LOADING);
        this.fireComponentLoaded();
        return this;
    }

    /**
     * Assign the given data to this EntityData.
     *
     * Performs a deep copy of a raw javascript object (or another EntityData) into the corresponding fields of this EntityData.
     * The deep copy relies on the get and set methods of the proxies for EntityData and EntityDataArray.
     * This method simply iterates over the specified data object, and assigns properties to this EntityData,
     * but only for persisted or important (decorated) properties.
     * PROVISIONAL components from a source EntityData are not copied.
     *
     * The status of this EntityData and all created descendants is set to ASSIGNED by default.
     * This means that a lazy resolution of the data may be requested.
     *
     * @param data
     * @param status
     * @param deepStatus
     * @param noReload
     * @returns {EntityData}
     */
    public assign(data: any, status: EntityDataStatus = EntityDataStatus.ASSIGNED, deepStatus: boolean = false, noReload: boolean = false): EntityData<T> {
        if (noReload) {
            EntityData.withNoReload(this);
        }
        this.ednStatus = status === EntityDataStatus.RETAIN ? data.ednStatus : status;
        for (let key of EntityData.getAllKeys(data)) {
            const value: any = (data["proxyTarget"] || data)[key];
            if ((value === null || value === undefined || value.ednStatus !== EntityDataStatus.PROVISIONAL)) {
                let targetKey: string;
                if (this.isAssignable(key)) {
                    targetKey = key;
                } else if (!data.entityDataType && this.isAssignable("_" + key)) {
                    targetKey = "_" + key;
                }
                if (targetKey) {
                    if (deepStatus) {
                        this[targetKey] = EntityData.withDeepStatus(value, status);
                    } else {
                        this[targetKey] = value;
                    }
                }
            }
        }
        EntityData.removeNoReload(this);

        if (status === EntityDataStatus.LOADING) {
            this.ednStatus = EntityDataStatus.LOADED;

            // assign() is called recursively to populate component members or array entries, so they will set themselves to LOADED status when their individual contents have been assigned.
            // Arrays themselves are not assigned, so we must set their status to LOADED explicitly.
            this.forEachComponentOrCollectionProperty((property: any, key: string) => {
                if (this.isEntityDataArray(key)) {
                    property.ednStatus = EntityDataStatus.LOADED;
                }
            });
        }
        if (!this.ednParent && this.getPath()) {
            this.rootPathResolved();
        }
        return this;
    }

    public isAssignable(key: string) {
        return this.canSetProperty(this, key) && (this.isPersisted(key) || this.isImportant(key));
    }

    public canSetProperty(obj: any, key: string) {
        if (obj == null) {
            return false;
        }
        const desc: PropertyDescriptor = Object.getOwnPropertyDescriptor(obj, key);

        if (!desc) {
            // property not defined directly — it might be on the prototype
            return this.canSetProperty(Object.getPrototypeOf(obj), key);
        }

        if ("writable" in desc) {
            // Data property
            return desc.writable === true;
        }

        // Accessor property
        return typeof desc.set === "function";
    }

    public get(fieldPath: string): any {
        return EntityData.getField(this, fieldPath);
    }

    public set(fieldPath: string, value: any): void {
        EntityData.setField(this, fieldPath, value);
    }

    private forEachComponentOrCollectionProperty(visit: (property: any, key: string) => void): void {
        // Note - earlier versions of ECMAScript didn't enumerate properties which were undefined, since they were effectively unknown to javascript.
        // With the implementation of javascript classes, this has changed.
        // As such, for components or collections which have not yet been populated, we need to avoid accessing them via the proxy,
        // as this would create them and trigger a lazy load request.
        for (let key of EntityData.getAllKeys(this)) {
            if (this.getEntityDataType(key)) {
                const property: any = this["proxyTarget"][key];
                if (property) {
                    visit(property, key);
                }
            }
        }
    }

    /**
     * Create a instance for a child
     * @param {string} fieldPath
     * @returns {EntityData<any>}
     */
    public createChildInstance(fieldPath: string): EntityData<any> {
        return fieldPath.split(".").reduce((entity, fieldName) => EntityData.create(entity.getEntityDataType(fieldName)), <EntityData<any>>this);
    }

    /**
     * Sets the reference to this EntityData within its parent to null
     */
    public removeFromParent(): void {
        if (this.ednParent && !(this.ednParent instanceof Array)) {
            this.ednParent[this.ednNodeName] = null;
        }
    }

    public removeProvisional(): void {
        this.forEachComponentOrCollectionProperty((property: any, key: string) => {
            if (property.ednStatus === EntityDataStatus.PROVISIONAL) {
                delete this[key];
            } else if (property.removeProvisional) {
                property.removeProvisional();
            }
        });
    }

    public markAsLoaded(): void {
        this.ednStatus = EntityDataStatus.LOADED;
        this.forEachComponentOrCollectionProperty((property: any) => property.markAsLoaded());
    }

    requestResolution(force = false): boolean {
        if (this.resolutionRequested && !force) {
            return true;
        }
        if (this.ednParent && this.ednParent.ednStatus != EntityDataStatus.LOADED && this.ednParent.ednStatus != EntityDataStatus.LOADING) {
            if (this.ednParent.requestResolution(force)) {
                return true;
            }
        }

        // We can only request direct resolution of an EntityData which has a defined and populated identifier
        if (!this.getIdentifierKey() || this.isTransient(this.getIdentifierKey())) {
            return false;
        }
        let identifier: string = this.getIdentifier();
        if (!identifier) {
            return false;
        }

        // An array entry has no node name, so its path already references it by its identifier.
        // For a component, however, the path references the node name. So if we are requesting direct resolution of a component, we must specify the identifier.
        let componentPath: string = this.getPath(true, false);
        let actualComponentPath: string = this.getPath(true, false, true);
        if (componentPath != undefined && componentPath !== "@transient" && this.ednNodeName) {
            componentPath += "@" + identifier;
            actualComponentPath += "@" + identifier;
        }

        this.resolutionRequested = true;
        if (componentPath && componentPath !== "@transient" && this.getRoot().ednComponentResolutionsObserver) {
            this.getRoot().ednComponentResolutionsObserver.next(new EntityDataResolutionRequest(componentPath, actualComponentPath));
        }
        return true;
    }

    /**
     * Converts this EntityData to a JSON string, filtering any transient/provisional data
     */
    public toJSONString(): string {
        return EntityData.stringify(this);
    }

    /**
     * Returns a promise of a Validation for this EntityData.
     * This method wraps ValidationService.createValidation(), and caches the validation locally once it is resolved.
     * Subsequent calls return a pre-resolved promise of the cached validation.
     * The promise returned by the initial invocation is also cached, and will be returned by subsequent calls until it is resolved.
     * @param validationService
     * @returns {Promise<Validation>}
     */
    public getValidation(validationService: ValidationService): Promise<Validation> {
        if (this.validation) {
            return Promise.resolve(this.validation);
        }
        if (!this.validationPromise) {
            this.validationPromise = validationService.createValidation(this);
            this.validationPromise.then(validation => this.validation = validation);
        }
        return this.validationPromise;
    }

    fireComponentLoaded(): void {
        this.onLoad();
        const path: string = this.getSubPath();
        if (path) {
            this.getRoot().$componentLoaded.next({path: path, component: this});
        }
    }

    /**
     * Subclasses can override to perform initialisation
     */
    onInit(): void {
    }

    /**
     * Subclasses can override to perform initialisation
     */
    onLoad(): void {
    }
}


/**
 * An array of EntityData objects.
 * Extends Array to provide handling of provisionally created arrays within an EntityData.
 * (This class in for internal use only - not exported).
 */
class EntityDataArray extends Array implements EntityDataNode {
    ednProxy: any;
    ednStatus: EntityDataStatus;
    ednParent: EntityDataNode;
    ednNodeName: string;
    ednUuid: string;

    private resolutionRequested: boolean;
    private entryDataType: typeof EntityData;

    /*
        constructor() {
            super();
            // Array extension does not work in ES5 - class is not added to prototype chain and object is just an array.
            // Workaround is to add the EntityDataArray prototype functions directly to the object.
            this.initNode || Object.getOwnPropertyNames(EntityDataArray.prototype).forEach((key: string) => this[key] = EntityDataArray.prototype[key]);
        }
    */

    // Built-in methods will use this as the constructor
    static override get [Symbol.species]() {
        return Array;
    }

    /**
     * Factory method for EntityDataArray creation - returns a proxy wrapped array.
     * NOTE: EntityDataArray objects should ALWAYS be created with this method.
     * @param baseEntityDataArray - If provided it will create the new EntityDataArray based in this value
     * @param parent
     * @param nodeName
     * @param status
     * @returns an empty array
     */
    static createAsNode(baseEntityDataArray: EntityDataArray, parent: EntityData<any>, nodeName?: string | symbol, status?: EntityDataStatus): EntityDataArray {
        let entityDataArray: EntityDataArray = new EntityDataArray().initNode(parent, nodeName, status);
        let proxy: EntityDataArray = new Proxy(entityDataArray, {

            /**
             * Provides the "proxyTarget" property, which gives access to the un-proxied array.
             * @param target
             * @param key
             * @returns {any}
             */
            get: (target: EntityDataArray, key: string | symbol) => {
                if (key === "proxyTarget") {
                    return target;
                }
                return target[key];
            },

            /**
             * Maintains the EntityDataNode properties for EntityData objects added to the array.
             * Ensures that any entries added to the array are appropriately typed, and if not, adds a new assigned EntityData in its place.
             * Once an entry is set in a provisional EntityDataArray object, the array is confirmed (marked as no longer provisional).
             * @param target
             * @param key
             * @param value
             * @param receiver
             * @returns true
             */
            set: (target: EntityDataArray, key: string | symbol, value: any, receiver: any) => {
                if (typeof key !== "string" || isNaN(+key)) {
                    target[key] = value;
                } else {
                    if (target.entryDataType) {
                        let entityData: EntityData<any>;
                        if (value instanceof target.entryDataType && (!value.ednParent || value.ednParent["proxyTarget"] === target || value.ednStatus === EntityDataStatus.LOADED)) {
                            // Use a cooked value directly if it is the right type and:
                            // - it has no parent; or
                            // - its parent is already set to this array; or
                            // - its status is LOADED (we don't want to trigger re-lazy-loading).
                            entityData = value;

                            // clean up any provisional data so that lazy loading can be triggered via the new parent if required
                            entityData.removeProvisional();
                            entityData.initNode(target.entryDataType, target, value.ednNodeName, value.ednStatus);
                        } else {
                            // Cook the raw object
                            // The status of the cooked object is LOADING if the target EntityData itself is LOADING, or else ASSIGNED
                            // Note however, that during EntityData.load(), this code should not be called as the whole array is assigned rather than individual elements.
                            entityData = EntityData.createAsNode(target.entryDataType, target.ednProxy, value.ednNodeName);
                            entityData.assign(value, target.ednStatus === EntityDataStatus.LOADING ? EntityDataStatus.LOADING : EntityDataStatus.ASSIGNED);
                        }
                        target[key] = entityData;
                        target.confirm(entityData.ednStatus);
                    } else {
                        target[key] = value;
                        target.confirm(target.ednStatus === EntityDataStatus.LOADING ? EntityDataStatus.LOADING : EntityDataStatus.ASSIGNED);
                    }
                }
                if (key !== "ednStatus" && target.ednParent.ednStatus !== EntityDataStatus.LOADING) {
                    target.fireCollectionChanged();
                }
                return true;
            }
        });
        entityDataArray.ednProxy = proxy;
        return <EntityDataArray>proxy;
    }

    initNode(
        parent: EntityData<any>,
        nodeKey?: string | symbol,
        status?: EntityDataStatus
    ): EntityDataArray {
        this.ednParent = parent;
        this.ednNodeName = typeof nodeKey === "string" ? nodeKey : undefined;
        this.ednUuid = "edn-" + StringUtils.uuid();
        this.ednStatus = status ? status : parent.ednStatus;
        this.entryDataType = parent.getEntityDataType(this.ednNodeName);
        return this;
    }

    confirm(status: EntityDataStatus): void {
        if (this.ednStatus === EntityDataStatus.PROVISIONAL) {
            this.ednStatus = status;
            this.ednParent.confirm(status);
        }
    }

    getRoot(): EntityData<any> {
        return this.ednParent ? this.ednParent.getRoot() : undefined;
    }

    convertToActualPath(path: string): string {
        return EntityData.createAsNode(this.entryDataType).convertToActualPath(path);
    }

    getPath(allowAnonymousParent: boolean = false, allowTransient: boolean = true, getActualPath: boolean = false): string {
        let nodeName: string = this.ednNodeName;
        if (!allowTransient && this.ednParent && this.ednParent instanceof EntityData && this.ednNodeName && (<EntityData<any>>this.ednParent).isTransient(this.ednNodeName)) {
            const pathKey: string = (<EntityData<any>>this.ednParent).getPathKey(this.ednNodeName)
            if (!pathKey) {
                return "@transient";
            }
            if (getActualPath) {
                nodeName = pathKey;
            }
        }
        let parentPath: string = this.ednParent.getPath(allowAnonymousParent, allowTransient, getActualPath);
        if (parentPath === "@transient") {
            return parentPath;
        }
        return parentPath ? parentPath + "/" + nodeName : undefined;
    }

    getSubPath(): string {
        let parentSubPath: string = this.ednParent.getSubPath();
        return this.ednNodeName ? (parentSubPath ? parentSubPath + "." + this.ednNodeName : this.ednNodeName) : parentSubPath;
    }

    /**
     * When the root path is resolved, if this array requires collection resolution, request the resolution.
     * Otherwise, pass the notification to any children.
     */
    rootPathResolved(): void {
        if (this.resolutionRequested) {
            this.requestResolution(true);
        } else if (this.ednStatus != EntityDataStatus.PROVISIONAL) {
            for (let entry of this) {
                if (entry.rootPathResolved) {
                    entry.rootPathResolved();
                }
            }
        }
    }

    loadCollection(path: string, json: Array<any>): void {
        let parts: string[] = path.split("/");

        // This is an array, so the path will start with the identifier of one of the array entries
        let identifier: string = parts.shift();
        if (!identifier || parts.length === 0) {
            return;
        }

        // Find the entry and assign to the collection within it
        for (let entry of this) {
            if (identifier == entry.getIdentifier(true)) {
                entry.loadCollection && entry.loadCollection(parts.join("/"), json);
                return;
            }
        }
    }

    loadComponent(path: string, json: any): void {
        let parts: string[] = path.split("/");

        // This is an array, so the path will start with the identifier of one of the array entries
        let identifier: string = parts.shift();
        if (!identifier) {
            return;
        }

        // Find the entry and assign to it
        for (let i = 0; i < this.length; i++) {
            let entry = this[i];
            if (EntityData.matchesIdentifier(identifier, entry.getIdentifier(true))) {
                // If this is the final path element, then it must reference an EntityData entry - replace it with a new EntityData entry
                if (parts.length === 0) {
                    if (entry instanceof EntityData) {
                        // set the status of the array to loading so the item will be assigned as loading to avoid infinite loop
                        const savedStatus: EntityDataStatus = this.ednStatus;
                        this.ednStatus = EntityDataStatus.LOADING;
                        this[i] = json; // proxy setter will create EntityData and assign
                        this.ednStatus = savedStatus;
                        this[i].fireComponentLoaded();
                    }
                } else {
                    entry.loadComponent && entry.loadComponent(parts.join("/"), json);
                }
                return;
            }
        }
    }

    removeProvisional(): void {
        for (let entry of this) {
            entry.removeProvisional();
        }
    }

    public markAsLoaded(): void {
        this.ednStatus = EntityDataStatus.LOADED;
        for (let entry of this) {
            entry.markAsLoaded();
        }
    }

    requestResolution(force = false): boolean {
        if (this.resolutionRequested && !force) {
            return true;
        }
        if (this.ednParent && this.ednParent instanceof EntityData && this.ednNodeName && (<EntityData<any>>this.ednParent).isTransient(this.ednNodeName)) {
            return false;
        }
        if (this.ednParent && this.ednParent.ednStatus != EntityDataStatus.LOADED) {
            return this.ednParent.requestResolution(force);
        }
        let collectionPath: string = this.getPath(false, false);
        let actualCollectionPath: string = this.getPath(false, false, true);
        this.resolutionRequested = true;
        if (collectionPath && collectionPath !== "@transient" && actualCollectionPath !== "@transient" && this.getRoot().ednCollectionResolutionsObserver) {
            this.getRoot().ednCollectionResolutionsObserver.next(new EntityDataResolutionRequest(collectionPath, actualCollectionPath));
        }
        return true;
    }

    fireCollectionLoaded(): void {
        const path: string = this.getSubPath();
        if (path) {
            this.getRoot().$collectionLoaded.next({path: path, collection: this});
        }
    }

    fireCollectionChanged(): void {
        const path: string = this.getSubPath();
        if (path) {
            this.getRoot().$collectionChanged.next({path: path, collection: this});
        }
    }
}
